import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import api from 'state/axios';
import { Box, Typography, CircularProgress, Tooltip, Chip } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { hasPermission } from 'state/checkPermission';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import { boxStyles as styles } from 'components/CustomStyles';
import ReportCard from './ReportCard';


const ShiftReports = () => {
    useAuthCheck();
    const navigate = useNavigate();
    const user = useSelector((state) => state.user);

    const { startDate, endDate } = useDateRangeContext();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reports, setReports] = useState([]);
    const [shiftFilter, setShiftFilter] = useState(null);

    const canCreate = hasPermission(user?.permissions, "ShiftReports", "create", false);


    const fetchReports = useCallback(async () => {
        if (!startDate || !endDate) return;
        try {
            setError(null);
            setLoading(true);
            const res = await api.post('/shiftreports', { startDate, endDate });
            setReports(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    const availableShifts = useMemo(() => {
        const seen = new Set();
        return reports
            .map(r => r.shift)
            .filter(s => s && !seen.has(s._id) && seen.add(s._id));
    }, [reports]);

    const filteredReports = useMemo(() =>
        shiftFilter
            ? reports.filter(r => r.shift?._id === shiftFilter)
            : reports,
        [reports, shiftFilter]
    );

    const handleReportDeleted = (id) => {
        setReports(prev => prev.filter(r => r._id !== id));
    };


    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title="Shift Reports"
                    subtitle={loading ? 'Loading...' : 'List of Reports'}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        {canCreate && (
                            <Tooltip title="Create" arrow>
                                <span>
                                    <StyledButton onClick={() => navigate("/shiftreports/new")}>
                                        <AddCircleOutlineIcon />
                                    </StyledButton>
                                </span>
                            </Tooltip>
                        )}
                    </Box>
                )}
            </FlexBetween>

            {loading ? (
                <FlexCenter sx={styles}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter sx={styles}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box>
                    {/* Filter bar */}
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
                        <Typography variant="caption" color="textSecondary">
                            {filteredReports.length} report{filteredReports.length !== 1 ? 's' : ''}
                        </Typography>
                        <Chip
                            label="All"
                            size="small"
                            variant={shiftFilter === null ? 'filled' : 'outlined'}
                            onClick={() => setShiftFilter(null)}
                        />
                        {availableShifts.map(shift => (
                            <Chip
                                key={shift._id}
                                label={shift.shiftName || shift.shiftCode}
                                size="small"
                                variant={shiftFilter === shift._id ? 'filled' : 'outlined'}
                                onClick={() => setShiftFilter(prev => prev === shift._id ? null : shift._id)}
                                sx={shiftFilter === shift._id ? { bgcolor: shift.color, color: '#fff', borderColor: shift.color } : { borderColor: shift.color, color: shift.color }}
                            />
                        ))}
                    </Box>

                    {/* Report list */}
                    <Box
                        sx={{
                            // height: LAYOUT_HEIGHT,
                            height: '72vh',
                            overflowY: 'auto',
                        }}
                    >
                        {filteredReports.length > 0 ? (
                            filteredReports.map((report) => (
                                <ReportCard
                                    key={report._id}
                                    report={report}
                                    onDeleted={handleReportDeleted}
                                />
                            ))
                        ) : (
                            <FlexCenter sx={{ ...styles, height: '72vh' }}>
                                <Typography color="textSecondary">No reports found</Typography>
                            </FlexCenter>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default ShiftReports;
